import type { Document } from "@/api/models"

/** Mirrors the backend's maximum extracted preview body size. */
const MAX_CITATION_OFFSET = 8 * 1024 * 1024
/** Bound visual emphasis independently of the full readable preview size. */
const MAX_CITATION_HIGHLIGHT_LENGTH = 16_384

export type CitationTextAnchor = { charStart: number; charEnd: number }

export function getCitationTextAnchor(document: Document | null): CitationTextAnchor | null {
  const charStart = document?.extra_data?.filth_char_start
  const charEnd = document?.extra_data?.filth_char_end
  return typeof charStart === "number" &&
    typeof charEnd === "number" &&
    Number.isSafeInteger(charStart) &&
    Number.isSafeInteger(charEnd) &&
    charStart >= 0 &&
    charStart <= charEnd &&
    charEnd <= MAX_CITATION_OFFSET
    ? { charStart, charEnd }
    : null
}

export function getCitationAnchorPage(document: Document | null): number | null {
  const page = document?.extra_data?.filth_page_number
  return typeof page === "number" && Number.isSafeInteger(page) && page >= 1 && page <= 1_000_000
    ? page
    : null
}

/** FILTH/Python offsets count Unicode code points; JS slices count UTF-16 units. */
export function getCitationTextPassage(
  content: string,
  anchor: CitationTextAnchor,
  quoteSnippet = ""
) {
  const { charStart, charEnd } = anchor
  if (
    !Number.isSafeInteger(charStart) ||
    !Number.isSafeInteger(charEnd) ||
    charStart < 0 ||
    charEnd <= charStart ||
    charEnd > MAX_CITATION_OFFSET ||
    charEnd - charStart > MAX_CITATION_HIGHLIGHT_LENGTH
  ) {
    return null
  }
  let position = 0
  let start = 0
  let end = 0
  for (const char of content) {
    if (position === charStart) start = end
    if (position === charEnd) break
    end += char.length
    position++
  }
  if (position !== charEnd) return null
  const passage = content.slice(start, end)
  // A quote can expose extraction/revision drift even when offsets remain in bounds.
  // Prefer readable, unmarked text over a confidently wrong highlight.
  const quote = quoteSnippet.replace(/\s+/g, " ").trim()
  if (quote) {
    const normalizedPassage = passage.replace(/\s+/g, " ").trim()
    if (
      !normalizedPassage ||
      (!normalizedPassage.includes(quote) && !quote.includes(normalizedPassage))
    ) {
      return null
    }
  }
  return { before: content.slice(0, start), passage, after: content.slice(end) }
}
